// Procedural organism IDENTITY — every user's organism is purposefully different.
//
// Until the personality observers feed real signal into the palette, a user's
// organism is derived DETERMINISTICALLY from a stable identity string (their
// email / id). The same string always yields the same organism, and different
// strings yield distinct hue families, harmonies, saturation, contrast, and a
// distinct FORM seed — so no two users meet the same organism, and the rule is
// enforced for existing accounts automatically (no migration: it's a pure
// function of who they already are).
//
// PHASE 2 (observer organism, minting) — we no longer mint from the full hue
// wheel, which could land a user on a muddy, off-brand colour. Instead every
// user is seeded onto one of a CURATED set of known-good palettes (hand-tuned
// hue + harmony + saturation/lightness/contrast), then nudged by a small,
// deterministic per-user jitter so two people on the same palette still differ
// without either ever leaving the tasteful band. The seed stays the floor of
// uniqueness; observers can later steer WITHIN a palette from real data.

import { harmonize, type ColorState, type Harmony } from "./color";

/** FNV-1a → 32-bit unsigned int. Deterministic and stable across runs/engines. */
export function seedFromString(s: string): number {
  let h = 2166136261;
  const str = s && s.length ? s : "innernet";
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Normalise an identity to a stable key so the organism a user meets is the
 *  SAME everywhere it's derived (dashboard shell, page headers, avatars). An
 *  email and its local-part must map to one organism, never two. */
export function identityKey(s: string | null | undefined): string {
  const v = (s ?? "").trim().toLowerCase();
  if (!v) return "innernet";
  return v.includes("@") ? v.split("@")[0] : v;
}

/** The user's display handle — the local-part of their email, the SAME key the
 *  organism is seeded from. So the account a write is attributed to reads the
 *  same wherever it's shown (avatar, activity feed). One derivation, not two. */
export function usernameFromEmail(email: string | null | undefined): string {
  return identityKey(email);
}

// ----------------------------------------------------------------------------
// Curated mint palettes — each is a known-good combination chosen to render as
// a refined MULTI-HUE watercolour (a body hue, a harmony rule that places the
// secondary/tinge, and a saturation/lightness/contrast inside the "beautiful"
// band: sat .50–.66, light .48–.58, contrast .58–.76). Spread across the wheel
// so the population reads as varied, but no single one is garish or muddy.
//
// MULTI-HUE BY DEFAULT (Task 4): minting is biased AWAY from monochromatic
// choices toward harmonies that span a real hue spread — triadic, split-comp,
// complementary, and the now-widened analogous fan. No palette here is mono, so
// every user's organism shows several hues at once (body/secondary/tinge across
// the wheel, plus the contrasting warm core + shifted halo derivePalette adds).
// ----------------------------------------------------------------------------
type MintPalette = { hue: number; harmony: Harmony; sat: number; light: number; contrast: number };

const MINT_PALETTES: MintPalette[] = [
  { hue: 104, harmony: "triadic",       sat: 0.62, light: 0.5,  contrast: 0.72 }, // leaf green · sky · amber (the showpiece family)
  { hue: 158, harmony: "split-comp",    sat: 0.58, light: 0.5,  contrast: 0.7  }, // emerald · coral · plum
  { hue: 188, harmony: "split-comp",    sat: 0.6,  light: 0.52, contrast: 0.66 }, // teal · rose · gold
  { hue: 202, harmony: "complementary", sat: 0.58, light: 0.54, contrast: 0.68 }, // sky · warm sand
  { hue: 224, harmony: "split-comp",    sat: 0.56, light: 0.54, contrast: 0.66 }, // cornflower · peach · lime
  { hue: 250, harmony: "triadic",       sat: 0.56, light: 0.56, contrast: 0.66 }, // periwinkle · coral · spring
  { hue: 274, harmony: "triadic",       sat: 0.56, light: 0.54, contrast: 0.68 }, // violet · spring · gold
  { hue: 300, harmony: "split-comp",    sat: 0.54, light: 0.55, contrast: 0.66 }, // orchid · sage · amber
  { hue: 328, harmony: "complementary", sat: 0.58, light: 0.55, contrast: 0.68 }, // rose · teal
  { hue: 344, harmony: "triadic",       sat: 0.6,  light: 0.55, contrast: 0.66 }, // blush · gold · teal
  { hue: 12,  harmony: "split-comp",    sat: 0.64, light: 0.53, contrast: 0.72 }, // vermilion · teal · violet
  { hue: 28,  harmony: "complementary", sat: 0.64, light: 0.54, contrast: 0.7  }, // amber · sky
  { hue: 42,  harmony: "triadic",       sat: 0.62, light: 0.55, contrast: 0.7  }, // gold · rose · teal
  { hue: 64,  harmony: "split-comp",    sat: 0.56, light: 0.52, contrast: 0.68 }, // chartreuse · violet · coral
  { hue: 84,  harmony: "triadic",       sat: 0.58, light: 0.51, contrast: 0.7  }, // spring green · violet · orange
  { hue: 134, harmony: "complementary", sat: 0.58, light: 0.5,  contrast: 0.72 }, // forest · magenta
];

/** The palette a SEED NUMBER lands on. This is the whole derivation; the
 *  string form below is just this with the hashing done first.
 *
 *  It exists because a person's seed is publishable (`profiles.organism_seed`)
 *  while their identity string is not: a room shows a member's netti by feeding
 *  the number they published back through the SAME function they were grown
 *  from, so it is their one creature and not a lookalike (law_one_netti).
 *  Nothing may derive a person's organism from anything else. */
export function organismFromHash(h: number): ColorState {
  const p = MINT_PALETTES[h % MINT_PALETTES.length];
  // deterministic sub-seed jitter from higher bits (never reuses the index bits)
  const hueJit = (((h >>> 8) % 15) - 7); // -7 .. +7 degrees
  const satJit = (((h >>> 13) % 7) - 3) / 100; // -0.03 .. +0.03
  const lightJit = (((h >>> 17) % 7) - 3) / 100; // -0.03 .. +0.03
  const contrastJit = (((h >>> 21) % 9) - 4) / 100; // -0.04 .. +0.04
  const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
  return harmonize(p.hue + hueJit, p.harmony, {
    sat: clamp(p.sat + satJit, 0.5, 0.68),
    light: clamp(p.light + lightJit, 0.47, 0.58),
    contrast: clamp(p.contrast + contrastJit, 0.56, 0.78),
  });
}

/** A deterministic, tasteful ColorState unique to a stable identity string.
 *  Lands the user on one curated palette, then applies a small per-user jitter
 *  (hue ±7°, sat/light/contrast within the band) so same-palette users still
 *  read as distinct without ever leaving the known-good range. */
export function organismFromSeed(identity: string): ColorState {
  return organismFromHash(seedFromString(identityKey(identity)));
}
